import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Form, FormSpy, Field } from "react-final-form";
import { TextInput, ToolTip } from "@truevault/aquilo";
import _ from "lodash";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { OrganizationDto } from "../../../../../../common/service/server/dto/OrganizationDto";
import { PrivacyCenterDto } from "../../../../../../common/service/server/dto/PrivacyCenterDto";

// Component to handle FormSpy with debouncing
const FormSpyWithDebounce = ({
  debouncedOnChange,
  previousValuesRef,
  customFields,
}: {
  debouncedOnChange: ((values: PersonalIdentifiersData) => void) | null;
  previousValuesRef: React.MutableRefObject<string>;
  customFields: Array<{ label: string; value: string }>;
}) => {
  const lastSerializedRef = useRef<string>("");

  if (!debouncedOnChange) {
    return null;
  }

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        // Merge form values with custom fields (which aren't part of the form)
        // Also copy new field names to old field names for backward compatibility
        const valuesData = {
          ...values,
          // Ensure both old and new field names are populated
          phone: values.subjectFreePhone || values.phone || "",
          postalAddress: values.subjectFreeTextAddress || values.postalAddress || "",
          customFields: customFields,
        } as PersonalIdentifiersData;
        const serializedValues = JSON.stringify(valuesData);

        // Only call debouncedOnChange if values actually changed
        if (serializedValues !== lastSerializedRef.current) {
          lastSerializedRef.current = serializedValues;

          // Only update previousValuesRef and call debouncedOnChange if different from what we last emitted
          if (serializedValues !== previousValuesRef.current) {
            previousValuesRef.current = serializedValues;
            debouncedOnChange(valuesData);
          }
        }

        return null;
      }}
    </FormSpy>
  );
};

export interface PersonalIdentifiersData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  subjectFreePhone?: string;
  postalAddress?: string;
  subjectFreeTextAddress?: string;
  onlineIdentifiers?: string;
  customFields?: Array<{ label: string; value: string }>;
}

interface PersonalIdentifiersFormProps {
  request: DataSubjectRequestDetailDto;
  organization?: OrganizationDto;
  privacyCenter?: PrivacyCenterDto;
  onChange?: (values: PersonalIdentifiersData) => void;
  onCustomIdentifierRowCountChange?: (rowCount: number) => void;
}

export const PersonalIdentifiersForm = ({
  request,
  organization,
  onChange,
  onCustomIdentifierRowCountChange,
}: PersonalIdentifiersFormProps) => {
  // Extract custom identifiers (excluding address parts and phone number)
  const customIdentifiers = useMemo(() => {
    const excludedKeys = new Set([
      "address1",
      "address2",
      "city",
      "state",
      "zipCode",
      "country",
      "phoneNumber",
    ]);

    const customFields: Array<{ label: string; value: string }> = [];

    // Check for legacy organizationCustomInput field
    if (
      request.organizationCustomInput &&
      organization?.customFieldEnabled &&
      organization?.customFieldLabel
    ) {
      customFields.push({
        label: organization.customFieldLabel,
        value: request.organizationCustomInput,
      });
    }

    // Use subjectCustomFieldsFmt for display names if available, otherwise fall back to subjectCustomFields
    if (request.subjectCustomFieldsFmt) {
      Object.entries(request.subjectCustomFieldsFmt).forEach(([label, value]) => {
        // Convert label to key format to check exclusions (e.g., "Address (Line 1)" -> "address1")
        const keyToCheck = label
          .toLowerCase()
          .replace(/\s+/g, "")
          .replace(/[()]/g, "")
          .replace(/line1/g, "1")
          .replace(/line2/g, "2");

        // Skip if it's an excluded field or if it's in the standard address-related or phone fields
        const isExcluded =
          excludedKeys.has(keyToCheck) ||
          label.toLowerCase().includes("address") ||
          label.toLowerCase().includes("city") ||
          label.toLowerCase().includes("state") ||
          label.toLowerCase().includes("zip") ||
          label.toLowerCase().includes("country") ||
          label.toLowerCase().includes("phone");

        if (!isExcluded && value) {
          customFields.push({ label, value });
        }
      });
    } else if (request.subjectCustomFields) {
      Object.entries(request.subjectCustomFields).forEach(([key, value]) => {
        if (!excludedKeys.has(key) && value) {
          // Convert camelCase to Title Case for display
          const label = key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
          customFields.push({ label, value });
        }
      });
    }

    return customFields;
  }, [
    request.subjectCustomFields,
    request.subjectCustomFieldsFmt,
    request.organizationCustomInput,
    organization?.customFieldEnabled,
    organization?.customFieldLabel,
  ]);

  // Calculate the number of rows for custom identifiers
  // Single field = 1 row, multiple fields = 2 per row (rounded up)
  const customIdentifierRowCount = useMemo(() => {
    if (customIdentifiers.length === 0) return 0;
    if (customIdentifiers.length === 1) return 1;
    return Math.ceil(customIdentifiers.length / 2);
  }, [customIdentifiers.length]);

  // Notify parent when custom identifier row count changes
  useEffect(() => {
    if (onCustomIdentifierRowCountChange) {
      onCustomIdentifierRowCountChange(customIdentifierRowCount);
    }
  }, [customIdentifierRowCount, onCustomIdentifierRowCountChange]);

  const initialValues: PersonalIdentifiersData = useMemo(() => {
    // Phone number can be in:
    // 1. subjectFreePhone (edited/free-text phone - takes precedence)
    // 2. subjectPhoneNumber (direct field)
    // 3. subjectCustomFields["phoneNumber"] (custom field by code)
    // 4. subjectCustomFieldsFmt["Phone Number"] (custom field by display name)
    // Always populate subjectFreePhone so edits always save to the free text field
    let phoneNumber = request.subjectFreePhone || "";
    if (!phoneNumber) {
      phoneNumber = request.subjectPhoneNumber || "";

      // Check custom fields for phone number if not in direct field
      if (!phoneNumber && request.subjectCustomFields?.phoneNumber) {
        phoneNumber = request.subjectCustomFields.phoneNumber;
      }

      // Check formatted custom fields for phone number
      if (!phoneNumber && request.subjectCustomFieldsFmt) {
        const phoneField = Object.entries(request.subjectCustomFieldsFmt).find(([label]) =>
          label.toLowerCase().includes("phone"),
        );
        if (phoneField) {
          phoneNumber = phoneField[1];
        }
      }
    }

    // Postal address can be in subjectMailingAddress (direct field) or subjectCustomFields (custom fields)
    // Common address custom field codes: address1, address2, city, state, zipCode, country
    // Always populate subjectFreeTextAddress so edits always save to the free text field
    let postalAddress = request.subjectFreeTextAddress || "";
    if (!postalAddress) {
      postalAddress = request.subjectMailingAddress || "";
      if (!postalAddress && request.subjectCustomFields) {
        // Try to construct address from custom fields if available
        const addressParts = [
          request.subjectCustomFields["address1"],
          request.subjectCustomFields["address2"],
          request.subjectCustomFields["city"],
          request.subjectCustomFields["state"],
          request.subjectCustomFields["zipCode"],
          request.subjectCustomFields["country"],
        ].filter(Boolean);
        if (addressParts.length > 0) {
          postalAddress = addressParts.join(", ");
        }
      }
    }

    return {
      firstName: request.subjectFirstName || "",
      lastName: request.subjectLastName || "",
      email: request.subjectEmailAddress || "",
      phone: phoneNumber, // Keep for backward compatibility
      subjectFreePhone: phoneNumber, // Always use free text field for edits
      postalAddress: postalAddress, // Keep for backward compatibility
      subjectFreeTextAddress: postalAddress, // Always use free text field for edits
      onlineIdentifiers: request.subjectOnlineIdentifiers || "",
      customFields: customIdentifiers, // Include custom fields for the TXT file
    };
  }, [
    request.subjectFirstName,
    request.subjectLastName,
    request.subjectEmailAddress,
    request.subjectPhoneNumber,
    request.subjectFreePhone,
    request.subjectMailingAddress,
    request.subjectFreeTextAddress,
    request.subjectOnlineIdentifiers,
    request.subjectCustomFields,
    request.subjectCustomFieldsFmt,
    request.organizationCustomInput,
    customIdentifiers,
  ]);

  // Lock phone/address fields ONLY if data came from custom fields (consumer-submitted forms)
  // Never lock based on subjectPhoneNumber or subjectMailingAddress alone, as these can be
  // manually edited and should always save to the free text fields (subjectFreePhone/subjectFreeTextAddress)
  const hasPhoneInCustomFields = !!(
    request.subjectCustomFields?.phoneNumber ||
    (request.subjectCustomFieldsFmt &&
      Object.keys(request.subjectCustomFieldsFmt).some((key) =>
        key.toLowerCase().includes("phone"),
      ))
  );
  const isPhoneLocked = hasPhoneInCustomFields;

  const hasAddressInCustomFields = !!(
    request.subjectCustomFields?.address1 ||
    request.subjectCustomFields?.address2 ||
    request.subjectCustomFields?.city ||
    request.subjectCustomFields?.state ||
    request.subjectCustomFields?.zipCode ||
    request.subjectCustomFields?.country ||
    (request.subjectCustomFieldsFmt &&
      Object.keys(request.subjectCustomFieldsFmt).some(
        (key) =>
          key.toLowerCase().includes("address") ||
          key.toLowerCase().includes("city") ||
          key.toLowerCase().includes("state") ||
          key.toLowerCase().includes("zip") ||
          key.toLowerCase().includes("country"),
      ))
  );
  const isAddressLocked = hasAddressInCustomFields;

  const lastEmittedValuesRef = useRef<string>(JSON.stringify(initialValues));
  const previousValuesRef = useRef<string>(JSON.stringify(initialValues));

  // Initialize refs when initialValues change (but only if they match what we last saved)
  // This handles the case where refreshRequest() brings back saved data
  useEffect(() => {
    const serializedInitial = JSON.stringify(initialValues);
    // Only update if the new initial values match what we last saved
    // This means the backend returned our saved data, so we should update the ref
    if (serializedInitial === lastEmittedValuesRef.current) {
      lastEmittedValuesRef.current = serializedInitial;
      previousValuesRef.current = serializedInitial;
    }
  }, [initialValues]);

  // Create debounced onChange callback for saving while typing (similar to privacy center pattern)
  const debouncedOnChange = useCallback(
    onChange
      ? _.debounce((values: PersonalIdentifiersData) => {
          const serializedValues = JSON.stringify(values);
          // Only call onChange if values actually changed
          if (serializedValues !== lastEmittedValuesRef.current) {
            onChange(values);
            lastEmittedValuesRef.current = serializedValues;
          }
        }, 750)
      : null,
    [onChange],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debouncedOnChange) {
        debouncedOnChange.cancel();
      }
    };
  }, [debouncedOnChange]);

  const tooltipText =
    "This info was submitted by the consumer; you should only process this request if it matches the records you have.";

  // Helper component to conditionally wrap with tooltip
  const ConditionalTooltip = ({
    shouldShow,
    children,
  }: {
    shouldShow: boolean;
    children: React.ReactNode;
  }) => {
    if (shouldShow) {
      return (
        <ToolTip text={tooltipText} maxWidth="300px" yOffset={100}>
          <div>{children}</div>
        </ToolTip>
      );
    }
    return <>{children}</>;
  };

  return (
    <div className="tw-boundary">
      <Form
        onSubmit={() => {}} // No-op since this is just a display form
        initialValues={initialValues}
        keepDirtyOnReinitialize
        render={() => (
          <div className="flex-col">
            {/* FormSpy to handle onChange callback with debouncing */}
            <FormSpyWithDebounce
              debouncedOnChange={debouncedOnChange}
              previousValuesRef={previousValuesRef}
              customFields={customIdentifiers}
            />

            {/* First Name and Last Name in same row - always disabled */}
            <div className="flex-container mb-sm">
              <div className="flex-grow mr-sm">
                <ConditionalTooltip shouldShow={true}>
                  <TextInput label="First Name" field="firstName" disabled={true} />
                </ConditionalTooltip>
              </div>
              <div className="flex-grow">
                <ConditionalTooltip shouldShow={true}>
                  <TextInput label="Last Name" field="lastName" disabled={true} />
                </ConditionalTooltip>
              </div>
            </div>

            {/* Email on its own row - always disabled */}
            <div className="mb-sm">
              <ConditionalTooltip shouldShow={true}>
                <TextInput label="Email" field="email" type="email" disabled={true} />
              </ConditionalTooltip>
            </div>

            {/* Phone Number on its own row - locked only if from custom fields */}
            <div className="mb-sm">
              <ConditionalTooltip shouldShow={isPhoneLocked}>
                <TextInput
                  label="Phone Number (if collected)"
                  field="subjectFreePhone"
                  type="tel"
                  disabled={isPhoneLocked}
                />
              </ConditionalTooltip>
            </div>

            {/* Postal Address on its own row with 4 lines - locked only if from custom fields */}
            <div className="mb-sm">
              <ConditionalTooltip shouldShow={isAddressLocked}>
                <Field name="subjectFreeTextAddress">
                  {({ input }) => (
                    <div>
                      <label
                        htmlFor="subjectFreeTextAddress"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Postal Address (if collected)
                      </label>
                      <textarea
                        {...input}
                        id="subjectFreeTextAddress"
                        disabled={isAddressLocked}
                        rows={4}
                        className="block w-full px-4 rounded-md py-1.5 sm:leading-6 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none disabled:placeholder:text-gray-300 disabled:ring-gray-200 disabled:text-gray-400 disabled:bg-neutral-0 placeholder:text-gray-400 resize-y"
                        placeholder=""
                      />
                    </div>
                  )}
                </Field>
              </ConditionalTooltip>
            </div>

            {/* Online Identifiers on its own row - always editable */}
            <div className="mb-sm">
              <TextInput
                label="Online Identifiers (if collected)"
                field="onlineIdentifiers"
                placeholder="IP addresses, device IDs, cookies, etc."
                disabled={false}
              />
            </div>

            {/* Custom Identifiers - layout depends on count */}
            {customIdentifiers.length > 0 && (
              <>
                {customIdentifiers.length === 1 ? (
                  // Single custom identifier: full width
                  <div className="mb-sm">
                    <ConditionalTooltip shouldShow={true}>
                      <div>
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          {customIdentifiers[0].label}
                        </label>
                        <input
                          type="text"
                          value={customIdentifiers[0].value}
                          disabled
                          className="block w-full px-4 rounded-md py-1.5 sm:leading-6 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none disabled:placeholder:text-gray-300 disabled:ring-gray-200 disabled:text-gray-400 disabled:bg-neutral-0 placeholder:text-gray-400"
                        />
                      </div>
                    </ConditionalTooltip>
                  </div>
                ) : (
                  // Multiple custom identifiers: two per row
                  <>
                    {customIdentifiers.map((field, index) => {
                      const isOdd = index % 2 === 0;
                      const isLastAndOdd = isOdd && index === customIdentifiers.length - 1;

                      if (isOdd) {
                        return (
                          <div key={index} className="flex-container mb-sm">
                            <div className={isLastAndOdd ? "w-full" : "flex-grow mr-sm"}>
                              <ConditionalTooltip shouldShow={true}>
                                <div>
                                  <label className="block text-sm font-medium leading-6 text-gray-900">
                                    {field.label}
                                  </label>
                                  <input
                                    type="text"
                                    value={field.value}
                                    disabled
                                    className="block w-full px-4 rounded-md py-1.5 sm:leading-6 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none disabled:placeholder:text-gray-300 disabled:ring-gray-200 disabled:text-gray-400 disabled:bg-neutral-0 placeholder:text-gray-400"
                                  />
                                </div>
                              </ConditionalTooltip>
                            </div>
                            {!isLastAndOdd && customIdentifiers[index + 1] && (
                              <div className="flex-grow">
                                <ConditionalTooltip shouldShow={true}>
                                  <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                      {customIdentifiers[index + 1].label}
                                    </label>
                                    <input
                                      type="text"
                                      value={customIdentifiers[index + 1].value}
                                      disabled
                                      className="block w-full px-4 rounded-md py-1.5 sm:leading-6 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none disabled:placeholder:text-gray-300 disabled:ring-gray-200 disabled:text-gray-400 disabled:bg-neutral-0 placeholder:text-gray-400"
                                    />
                                  </div>
                                </ConditionalTooltip>
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </>
                )}
              </>
            )}
          </div>
        )}
      />
    </div>
  );
};
